import React, { useState } from "react";
import Layout from "../components/Layout";

import dashboardImg from "../assets/img/dashboard.png";
import joblogImg from "../assets/img/joblog.png";
import routesImg from "../assets/img/routes.png";
import responsiveImg from "../assets/img/dashboard_lg.png";

function Features() {
  const [modalImage, setModalImage] = useState(null);

  const features = [
    {
      title: "Multi-Tenant Dashboard",
      desc: "Easily manage multiple programs, customers, consumers, and users under one secure platform. Each user sees only what they need through role-based access controls.",
      image: dashboardImg,
      benefit: "Save 5+ hours weekly on admin tasks",
      category: "management",
    },
    {
      title: "Service Tracking",
      desc: "Track job coaching activities, time, and outcomes with detailed logs. Entries are tied to authorizations and fully searchable for admin review.",
      image: joblogImg,
      benefit: "Eliminate paperwork errors completely",
      category: "tracking",
    },
    {
      title: "Mileage Logging",
      desc: "Plan and record mileage with mapped routes, consumer assignment, and signature confirmation. Logs are invoice-ready with reason-for-travel and stop history.",
      image: routesImg,
      benefit: "Accurate reimbursements every time",
      category: "tracking",
    },
    {
      icon: "🧾",
      title: "Invoice Support",
      desc: "Automatically generate documents and totals for invoices based on approved logs and mileage. Includes service breakdowns, rates, and PDF downloads with signatures.",
      benefit: "Get paid 40% faster with clean documentation",
      category: "billing",
    },
    {
      icon: "📆",
      title: "Plans & Monthly Reports",
      desc: "Capture service plans and monthly activity summaries. Add signatures from consumers, staff, and CRPs for billing compliance.",
      benefit: "Generate reports in minutes, not hours",
      category: "reporting",
    },
    {
      icon: "✍️",
      title: "Signature Capture",
      desc: "Collect secure, timestamped signatures from consumers and staff across logs, plans, and reports — on any device.",
      benefit: "Meet compliance requirements effortlessly",
      category: "compliance",
    },
    {
      icon: "📁",
      title: "Document Vault",
      desc: "Store and organize all service documentation, plans, invoices, and reports.",
      benefit: "Never lose important documents again",
      category: "management",
    },
    {
      icon: "🔐",
      title: "Role-Based Permissions",
      desc: "Set roles and access levels for each user type — admin, coach, customer, or support staff.",
      benefit: "Protect sensitive data automatically",
      category: "security",
    },
    {
      icon: "📊",
      title: "Dashboards & Analytics",
      desc: "Visualize hours logged, mileage paid, reports completed, and more using real-time widgets and charts.",
      benefit: "Make data-driven decisions instantly",
      category: "analytics",
    },
  ];

  const imageFeatureTitles = [
    "Multi-Tenant Dashboard",
    "Service Tracking",
    "Mileage Logging",
  ];

  return (
    <Layout>
      {/* Modern Hero Section */}
      <div className="bg-gradient-primary text-white py-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-8">
              <h1 className="display-4 fw-bold mb-3">
                Everything You Need to Run
                <span className="text-warning d-block">
                  Professional CRP Services
                </span>
              </h1>
              <p className="lead mb-4 text-light">
                WebVenly empowers CRPs, job coaches, administrators and
                customers with a flexible and secure platform tailored for
                real-world service delivery. Our system brings together tools
                for tracking, reporting, compliance, and communication — all in
                one place.
              </p>
              <div className="d-flex gap-3 flex-wrap">
                <span className="badge bg-white text-primary px-3 py-2 fs-6">
                  <i className="bi bi-check-circle-fill me-2"></i>
                  Purpose-built for CRPs
                </span>
                <span className="badge bg-white text-primary px-3 py-2 fs-6">
                  <i className="bi bi-shield-check me-2"></i>
                  Compliance Ready
                </span>
                <span className="badge bg-white text-primary px-3 py-2 fs-6">
                  <i className="bi bi-cloud me-2"></i>
                  Cloud-Based
                </span>
              </div>
            </div>
            <div className="col-lg-4 text-center mt-4 mt-lg-0">
              <img
                src={responsiveImg}
                className="img-fluid rounded-3 shadow-lg"
                alt="WebVenly Platform Overview"
                style={{ maxHeight: "400px" }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="container py-5">
        {/* Features Grid - Enhanced version of your existing layout */}
        <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
          {features.map((f, idx) => {
            const isImageBottom = imageFeatureTitles.includes(f.title);

            return (
              <div className="col d-flex" key={idx}>
                <div className="card shadow-sm h-100 feature-card-modern text-center d-flex flex-column justify-content-between">
                  <div className="card-body d-flex flex-column justify-content-between">
                    <div>
                      {!isImageBottom && (
                        <div className="display-5 mb-3">{f.icon}</div>
                      )}
                      <h5 className="card-title fw-bold">{f.title}</h5>
                      <p className="card-text mt-3 text-muted">{f.desc}</p>

                      {/* Add benefit badge */}
                      {f.benefit && (
                        <div className="mb-3">
                          <div className="badge bg-success bg-opacity-10 text-success px-3 py-2 w-100">
                            <i className="bi bi-lightning-fill me-2"></i>
                            <small className="fw-semibold">{f.benefit}</small>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Keep your existing image handling */}
                  {f.image && (
                    <div className="px-3 pb-3">
                      <button
                        type="button"
                        className="btn p-0 border-0 w-100 position-relative overflow-hidden rounded"
                        onClick={() => setModalImage(f.image)}
                      >
                        <img
                          src={f.image}
                          alt={f.title}
                          className="img-fluid rounded"
                          style={{
                            maxHeight: isImageBottom ? "260px" : "180px",
                            objectFit: isImageBottom ? "contain" : "cover",
                            width: "100%",
                          }}
                        />
                        {/* Add zoom overlay */}
                        <div className="position-absolute top-0 end-0 m-2">
                          <div
                            className="bg-dark bg-opacity-75 text-white rounded-circle p-2"
                            style={{ width: "32px", height: "32px" }}
                          >
                            <i
                              className="bi bi-zoom-in"
                              style={{ fontSize: "14px" }}
                            ></i>
                          </div>
                        </div>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Keep your existing modal - just enhance it slightly */}
        {modalImage && (
          <div
            className="modal show fade d-block"
            tabIndex="-1"
            onClick={() => setModalImage(null)}
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.85)",
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              zIndex: 1050,
              cursor: "pointer",
            }}
          >
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "100%" }}
            >
              <img
                src={modalImage}
                alt="Zoomed Feature"
                className="img-fluid rounded shadow-lg"
                onClick={(e) => e.stopPropagation()}
                style={{
                  maxHeight: "80vh",
                  maxWidth: "90vw",
                  objectFit: "contain",
                }}
              />
            </div>

            {/* Enhanced close button */}
            <button
              onClick={() => setModalImage(null)}
              className="btn btn-light position-absolute rounded-circle"
              style={{
                top: "1rem",
                right: "1rem",
                zIndex: 1100,
                width: "48px",
                height: "48px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        )}

        {/* Add the benefits section from the modern version */}
        <div className="row mt-5 pt-5">
          <div className="col-12">
            <div className="bg-gradient-light rounded-4 p-5">
              <div className="text-center mb-5">
                <h3 className="display-6 fw-bold mb-3">
                  Why Teams Choose WebVenly
                </h3>
                <p className="lead text-muted">
                  Built specifically for rehabilitation services workflow
                </p>
              </div>

              <div className="row g-4">
                {[
                  {
                    icon: "⚡",
                    title: "Purpose-Built",
                    desc: "Designed specifically for CRP vendors, not adapted from generic software",
                  },
                  {
                    icon: "📈",
                    title: "Scales With You",
                    desc: "From solo job coaches to multi-site operations - grows as you grow",
                  },
                  {
                    icon: "☁️",
                    title: "Cloud-Based",
                    desc: "Access from anywhere - office, field, or home with any internet device",
                  },
                  {
                    icon: "🤝",
                    title: "Dual-Tenant",
                    desc: "Serves both vendors AND state agencies for seamless collaboration",
                  },
                  {
                    icon: "🚀",
                    title: "Fast Reports",
                    desc: "Generate professional monthly reports in minutes instead of hours",
                  },
                  {
                    icon: "🛡️",
                    title: "Compliance Ready",
                    desc: "Built-in audit trails and documentation standards for any review",
                  },
                ].map((benefit, idx) => (
                  <div className="col-md-6 col-lg-4" key={idx}>
                    <div className="d-flex align-items-start gap-3">
                      <div
                        className="bg-white rounded-circle d-flex align-items-center justify-content-center shadow-sm flex-shrink-0"
                        style={{ width: "50px", height: "50px" }}
                      >
                        <span className="fs-4">{benefit.icon}</span>
                      </div>
                      <div>
                        <h6 className="fw-bold mb-2">{benefit.title}</h6>
                        <p className="text-muted mb-0 small">{benefit.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Add CTA section */}
        <div className="text-center mt-5 pt-5">
          <div className="bg-dark text-white rounded-4 p-5">
            <h3 className="display-6 fw-bold mb-3">
              Ready to Modernize Your Workflow?
            </h3>
            <p className="lead mb-4">
              Join CRP organizations who've already transformed their operations
              with WebVenly
            </p>
            <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
              <a href="/demo" className="btn btn-warning btn-lg px-4">
                <i className="bi bi-play-circle me-2"></i>
                See It In Action
              </a>
              <a href="/contact" className="btn btn-outline-light btn-lg px-4">
                <i className="bi bi-chat-dots me-2"></i>
                Talk to Our Team
              </a>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default Features;
